import React, { useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  EmptyState,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useNotificationDestinationsQuery } from 'hooks/queries/WebHooks/useNotificationDestinationsQuery'
import { ActionIds } from 'types/types'
import { NotificationDestination } from '../types'
import { DestinationsList } from './DestinationsList'
import { IntegrationsSheet } from './Sheet/IntegrationsSheet'

export const Destinations = () => {
  const { t } = useTranslation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [action, setAction] = useAtom(actionAtom)

  const onAddNewWebHook = () => {
    setAction({
      ...action,
      actionId: ActionIds?.HOOKS,
      header: t('integrations.addNewWebhook'),
      onClose: () => {
        setAction(undefined)
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  const {
    data: { content: destinationList } = {},
    isFetching,
    isError,
  } = useNotificationDestinationsQuery('', 50)

  const hasDestinations = destinationList?.length > 0

  const onEditDestination = (destination: NotificationDestination) => {
    setAction({
      ...action,
      actionId: ActionIds?.HOOKS,
      header: t('integrations.updateWebhook'),
      meta: {
        destination,
      },
      onClose: () => {
        setAction(undefined)
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <>
      <PageHeader
        description={t('integrations.hooksDescription')}
        renderRightSection={
          hasDestinations && (
            <Button
              onClick={onAddNewWebHook}
              name={t('integrations.addHook')}
              iconElement={<Icon name="Plus" />}
            >
              {t('integrations.addHook')}
            </Button>
          )
        }
      />

      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isFetching && isError && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
        />
      )}

      {!isFetching && !isError && !hasDestinations && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('integrations.noWebhooksMessage')}
          PrimaryButton={
            <Button name={t('integrations.addHook')} onClick={onAddNewWebHook}>
              {t('integrations.addHook')}
            </Button>
          }
          description={
            <>
              {t('integrations.hooksDescription')}{' '}
              <ExternalLink href="https://help.bvnk.com/hc/en-us/articles/22943700946450-Webhook-Configuration-in-the-BVNK-Portal">
                {t('learnMore')}
              </ExternalLink>
            </>
          }
        />
      )}

      {!isFetching && !isError && hasDestinations && (
        <Box className="justify-center items-center w-full">
          <DestinationsList destinations={destinationList} onEditDestination={onEditDestination} />
        </Box>
      )}

      <IntegrationsSheet isSheetOpen={isSheetOpen} />
    </>
  )
}
